import React, { useState, useMemo } from 'react';
import { Input } from '../ui/input';
import { evaluateFormula, formatComputed } from '../../lib/formCalc';
import { Sparkles, AlertTriangle } from 'lucide-react';

/**
 * Live preview panel for a formula.
 * Extracts `{field_id}` references from the expression, renders a sample-inputs
 * row so the admin can type numbers and see the live-computed result.
 *
 * Props:
 *   formula — the formula string (e.g. "{a} + {b} * 2")
 *   format  — optional format config ({type, decimals})
 *   context — 'field' | 'row' | 'table'
 *      'field' → references are {id}
 *      'row'   → also supports row.<key> references  (requires `rowColumns` = [{key,label,type}])
 *      'table' → also supports col.<key>.<agg>       (requires `rowColumns`)
 *   rowColumns — optional: array of columns from the parent calc_table (for row/table mode).
 */
export default function FormulaPreview({ formula, format, context = 'field', rowColumns = [] }) {
  const refs = useMemo(() => {
    const out = { fields: new Set(), cols: new Set() };
    if (!formula) return out;
    const fieldRe = /\{([a-zA-Z0-9_.-]+)\}/g;
    let m;
    while ((m = fieldRe.exec(formula)) !== null) out.fields.add(m[1]);
    if (context === 'row' || context === 'table') {
      const rowRe = /\brow\.([a-zA-Z0-9_]+)\b/g;
      while ((m = rowRe.exec(formula)) !== null) out.cols.add(m[1]);
    }
    return out;
  }, [formula, context]);

  const [sampleFields, setSampleFields] = useState({});
  const [sampleRow, setSampleRow] = useState({});

  const fieldIds = Array.from(refs.fields);
  const colKeys = Array.from(refs.cols);

  const result = useMemo(() => {
    if (!formula || !formula.trim()) return { val: null, error: null };
    try {
      const scope = { fields: sampleFields };
      if (context === 'row' || context === 'table') scope.row = sampleRow;
      if (context === 'table') scope.allRows = [sampleRow];
      const val = evaluateFormula(formula, scope);
      if (val === null && (fieldIds.length > 0 || colKeys.length > 0)) return { val: null, error: 'Invalid formula or division by zero' };
      return { val, error: null };
    } catch (e) {
      return { val: null, error: String(e) };
    }
  }, [formula, sampleFields, sampleRow, context, fieldIds.length, colKeys.length]);

  const showEmpty = !formula || !formula.trim();

  return (
    <div className="mt-2 p-2.5 bg-white border border-[#C7D2FE] rounded-lg" data-testid="formula-preview">
      <div className="flex items-center gap-1.5 text-[11px] font-medium text-[#4338CA] mb-1.5">
        <Sparkles className="w-3 h-3" />Live Preview
      </div>

      {showEmpty ? (
        <div className="text-[11px] text-[#94A3B8] italic">Type a formula above to see a live preview.</div>
      ) : (
        <>
          {(fieldIds.length > 0 || colKeys.length > 0) && (
            <div className="space-y-1.5 mb-2">
              {fieldIds.map(id => (
                <div key={id} className="flex items-center gap-2">
                  <span className="text-[10px] text-[#64748B] font-mono w-28 truncate">{`{${id}}`}</span>
                  <Input
                    type="number"
                    value={sampleFields[id] ?? ''}
                    onChange={(e) => setSampleFields(prev => ({ ...prev, [id]: e.target.value }))}
                    className="h-7 text-xs flex-1"
                    placeholder="sample"
                    data-testid={`preview-input-${id}`}
                  />
                </div>
              ))}
              {colKeys.map(k => (
                <div key={k} className="flex items-center gap-2">
                  <span className="text-[10px] text-[#64748B] font-mono w-28 truncate">{`row.${k}`}</span>
                  <Input
                    type="number"
                    value={sampleRow[k] ?? ''}
                    onChange={(e) => setSampleRow(prev => ({ ...prev, [k]: e.target.value }))}
                    className="h-7 text-xs flex-1"
                    placeholder="sample"
                    data-testid={`preview-row-${k}`}
                  />
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center justify-between border-t border-[#E2E8F0] pt-1.5">
            <span className="text-[10px] text-[#64748B]">Result</span>
            {result.error ? (
              <span className="text-[11px] text-[#991B1B] flex items-center gap-1"><AlertTriangle className="w-3 h-3" />{result.error}</span>
            ) : (
              <span className="text-sm font-mono font-semibold text-[#4338CA]" data-testid="formula-preview-result">
                {formatComputed(result.val, format || { type: 'number', decimals: 2 })}
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
}
